/**
 * useHalachicDate — React hook for memoized Hebrew date computation.
 *
 * Wraps the pure `computeHebrewDate` utility from @triedge/domain-core
 * and provides a reactive, memoized HebrewDateResult.
 *
 * Authority: apps/ystnj/documentation/prd/parent-portal/parnes-hayom/v1.1/implementation-plan.md
 */

import { useMemo } from 'react';
import { computeHebrewDate, type GeoConfig, type HebrewDateResult } from '@triedge/domain-core';

/**
 * Compute a memoized Hebrew date for a Gregorian date.
 *
 * @param gregorianDate - ISO date string (YYYY-MM-DD) or null
 * @param sunsetApplied - Whether the halachic date should be rolled forward (after sunset)
 * @param geoConfig - Optional geographic configuration for sunset-aware computation
 * @returns HebrewDateResult or null if no date is provided
 */
export const useHalachicDate = (
  gregorianDate: string | null | undefined,
  sunsetApplied: boolean,
  geoConfig?: GeoConfig
): HebrewDateResult | null => {
  return useMemo(() => {
    if (!gregorianDate) return null;
    return computeHebrewDate(gregorianDate, sunsetApplied, geoConfig);
  }, [gregorianDate, sunsetApplied, geoConfig?.latitude, geoConfig?.longitude, geoConfig?.timezone]);
};
